import { computed, hasInjectionContext, type ComputedRef } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { readPrices } from '@/api/billing/prices'
import { useSessionStore } from '@/apps/session/stores/session'
import { CATALOG_PRICES, priceBookOf, type PriceBook } from '../prices'

/** The prices' key. Not under the wallet's: a top-up invalidates the wallet and moves no price. */
export const priceKeys = {
  all: ['prices'] as const,
}

export interface UsePricesReturn {
  /** The organization's prices once read, the catalogue's before and outside an organization. */
  book: ComputedRef<PriceBook>
  /** The read is still in flight — the figures shown meanwhile are the catalogue's. */
  isLoading: ComputedRef<boolean>
}

/**
 * **The prices every estimate is computed at** (`docs/plans/org-pricing.md` OP7): the active
 * organization's, as `GET /orgs/current/prices` answers them.
 *
 * - **Every member asks**, the route taking no permission: an editor starting a transcription is
 *   shown its cost.
 * - **One read for the whole app**, cached like the wallet; the session guard clears the cache when
 *   the organization changes, so the entry is always the URL's organization's. A price changes on an
 *   operator's gesture, so a few minutes' staleness is what a screen can afford.
 * - **The catalogue until it answers**, outside an organization (nothing is asked then), and in a
 *   call made outside a component — a store's or a unit spec's —, where no query client is reachable.
 */
export function usePrices(): UsePricesReturn {
  if (!hasInjectionContext()) {
    return { book: computed(() => CATALOG_PRICES), isLoading: computed(() => false) }
  }
  const session = useSessionStore()
  const query = useQuery({
    queryKey: priceKeys.all,
    queryFn: ({ signal }) => readPrices(signal),
    enabled: computed(() => !!session.access?.org),
    staleTime: 5 * 60_000,
  })
  return {
    book: computed(() => (session.access?.org ? priceBookOf(query.data.value) : CATALOG_PRICES)),
    isLoading: computed(() => query.isLoading.value),
  }
}
